import { useCallback, useContext, useMemo } from "react";
import { ChatMessagesContext } from "src/chats/contexts/ChatMessagesContext";
import type { ChatMessage } from "src/chats/objects/ChatMessage";
import type { EventBusMessage } from "src/shared/eventbus/EventBusMessage";

export const useChatMessages = (chatUuids?: string[]) => {
  const chatEventBusMessages = useContext(ChatMessagesContext)!;
  const { messages: originalMessages, clearMessages: originalClearMessages } =
    chatEventBusMessages;

  const chatsFilterFunction = useMemo(() => {
    if (!chatUuids) {
      return null;
    }
    const chatUuidsSet = new Set(chatUuids);
    return (message: EventBusMessage<ChatMessage>) =>
      chatUuidsSet.has(message.body.chatUuid);
  }, [chatUuids]);

  const messages = useMemo(() => {
    if (!chatsFilterFunction) {
      return originalMessages;
    }
    return originalMessages.filter(chatsFilterFunction);
  }, [originalMessages, chatsFilterFunction]);

  const clearMessages = useCallback(() => {
    if (!chatsFilterFunction) {
      originalClearMessages();
    }
    originalClearMessages(chatsFilterFunction);
  }, [originalClearMessages, chatsFilterFunction]);

  return useMemo(
    () => ({
      ...chatEventBusMessages,
      messages,
      clearMessages,
    }),
    [chatEventBusMessages, messages, clearMessages],
  );
};
